import { Link, NavLink } from "react-router-dom";
import Cart from "svgs/cart";
import Header from "./Header";
import { IoSearch } from "react-icons/io5";
import { FiMenu } from "react-icons/fi";
import logo from "../../src/images/logo.png";
import DrawerMenu from "components/dashboardComponents/drawerMenu";
import { useEffect, useMemo, useState } from "react";
import useAllProductsData from "hook/useAllProductsData";
import Lottie from "lottie-react";
import flameAnimation from "../json/FlameAnimation.json";
// import { getCartCount, getCartUpdatedEventName } from "./utils/cart";
import {
  FaFireAlt,
  FaHome,
  FaSearch,
  FaShoppingCart,
  FaTags,
  FaTimesCircle,
  FaTruck,
} from "react-icons/fa";
import { getCartCount, getCartUpdatedEventName } from "utils/cart";

const normalizeText = (text = "") =>
  text.toString().toLowerCase().replace(/\s+/g, "").trim();

const Navbar = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { allProducts } = useAllProductsData();
  const [cartCount, setCartCount] = useState(() => getCartCount());

  useEffect(() => {
    const syncCartCount = () => setCartCount(getCartCount());
    const onStorage = () => syncCartCount();
    const eventName = getCartUpdatedEventName();

    const onCartUpdated = () => syncCartCount();
    window.addEventListener(eventName, onCartUpdated);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", syncCartCount);

    return () => {
      window.removeEventListener(eventName, onCartUpdated);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", syncCartCount);
    };
  }, []);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  /* 🔹 Pre-flatten variants (optimized) */
  const variants = useMemo(() => {
    return allProducts.flatMap((product) =>
      product.variants.map((variant) => ({
        variant_id: variant.variant_id,
        title: variant.title,
        title_normalized: normalizeText(variant.title),
        price: variant.price,
        flashPrice: variant.flashPrice,
        item_id: product.item_id,
        catalogue: product.catalogue,
        image: variant.images?.length ? variant.images[0] : null,
      })),
    );
  }, [allProducts]);

  /* 🔹 Search ONLY by variant.title */
  const searchResults = useMemo(() => {
    if (!searchText.trim()) return [];

    const keyword = normalizeText(searchText);

    return variants
      .filter((v) => v.title_normalized.includes(keyword))
      .slice(0, 10);
  }, [searchText, variants]);

  return (
    <>
      <nav className="sticky top-0 z-40 flex  items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 py-2 shadow-sm lg:px-5 lg:py-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 whitespace-nowrap min-w-[100px]"
        >
          <img
            src={logo}
            alt="Logo"
            className="h-8 sm:h-10 lg:h-12 w-auto rounded-full bg-white"
          />
        </Link>

        <div
          className="relative flex-1 min-w-0 items-center sm:mx-5
        "
        >
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label="Search products"
            className="w-full rounded-full border border-gray-100 bg-gray-100 px-10 py-1 pr-11 text-[0.82rem] outline-none focus:border-blue-500 lg:border-none lg:py-2.5 lg:text-[0.95rem]"
          />
          {searchText && (
            <div className="absolute z-50 bg-white border w-full mt-1 rounded shadow max-h-72 overflow-y-auto">
              {searchResults.length > 0 ? (
                searchResults.map((item) => (
                  <Link
                    key={item.variant_id}
                    to={`/details/${item.catalogue}/${item.item_id}`}
                    onClick={() => setSearchText("")}
                    className="flex items-center gap-2 p-2 hover:bg-gray-100"
                  >
                    <img
                      src={`https://backend.nuhababy.com/${item.image}`}
                      className="w-8 h-8 rounded object-cover"
                      alt={item.title}
                    />
                    <div>
                      <p className="text-xs font-medium">{item.title}</p>
                      <p className="text-[10px] text-gray-500">
                        ৳ {item.flashPrice}
                      </p>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="p-3 text-center text-xs text-gray-500">
                  No product found
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ml-auto hidden items-center gap-2.5 lg:flex">
          <Link
            to="/freeDeliveryProducts"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-2 text-[0.92rem] font-semibold text-slate-700 no-underline transition duration-200 hover:-translate-y-px hover:bg-slate-100"
          >
            <FaTruck />
            <span>Free Delivery</span>
          </Link>
          <Link
            to="/hotOffer"
            className="animate-hot-offer inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-2 text-[0.92rem] font-semibold text-slate-700 no-underline transition duration-200 hover:-translate-y-px hover:bg-slate-100"
          >
            <FaFireAlt />
            <span>Offer</span>
          </Link>
          <Link
            to="/cart"
            className="animate-cart-bounce relative inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-2 text-[0.92rem] font-semibold text-slate-700 no-underline transition duration-200 hover:-translate-y-px hover:bg-slate-100"
          >
            <FaShoppingCart />
            <span>Cart</span>
            {cartCount > 0 && (
              <span className="absolute -right-1.5 -top-1.5 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            )}
          </Link>
        </div>
      </nav>
      {/*  for mobile screen */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t border-slate-200 bg-white py-2 shadow-[0_-3px_12px_rgba(0,0,0,0.12)] lg:hidden">
        <NavLink
          to="/"
          className={({ isActive }) =>
            `inline-flex flex-col items-center gap-1 text-[11px] font-semibold ${
              isActive ? "text-blue-600" : "text-slate-700"
            }`
          }
        >
          <FaHome className="text-base" />
          Home
        </NavLink>

        <NavLink
          to="/freeDeliveryProducts"
          className={({ isActive }) =>
            `inline-flex flex-col items-center gap-1 text-[11px] font-semibold ${
              isActive ? "text-blue-600" : "text-slate-700"
            }`
          }
        >
          <FaTruck className="text-base" />
          Free
        </NavLink>

        <NavLink
          to="/hotOffer"
          className={({ isActive }) =>
            `inline-flex flex-col items-center gap-1 text-[11px] font-semibold ${
              isActive ? "text-blue-600" : "text-slate-700"
            }`
          }
        >
          <FaTags className="text-base" />
          Offer
        </NavLink>

        <NavLink
          to="/cart"
          className={({ isActive }) =>
            `relative inline-flex flex-col items-center gap-1 text-[11px] font-semibold ${
              isActive ? "text-blue-600" : "text-slate-700"
            }`
          }
        >
          <FaShoppingCart className="text-base" />
          Cart
          {cartCount > 0 && (
            <span className="absolute -right-2 top-0 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-bold text-white">
              {cartCount > 99 ? "99+" : cartCount}
            </span>
          )}
        </NavLink>
      </nav>
    </>
  );
};

export default Navbar;
